import { useEffect, useState, useRef } from "react";
import {
    Card,
    CardBody,
    CardFooter,
    Checkbox,
    Button,
    Alert,
    Typography,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link, useSearchParams } from "react-router";
import {
    authSelector,
    fetchSession,
    login,
    totpSetup,
    totpVerify,
} from "store/auth";
import { AUTH_STATUS, AUTH_ACTION } from "store/auth-status";
import Image from "components/image";
import logo from "assets/img/logos/gobby-anim.gif";
import { fetcherSimple, fetcherFull } from "utils/api";
import { toast } from "react-toastify";
import Trademark from "components/Trademark";
import TotpVerifyDialog from "components/dialog/totp-verify-dialog";
import TotpSetupDialog from "components/dialog/totp-setup-dialog";
import RHFInputIcon from "components/form/rhf-input-icon";
import { sanitiseInput, useRateLimit } from "utils/utils.jsx";
import { isApiError, reportError, getApiErrorMessage, classifyError } from "utils/api-error";

const validationSchemaLogin = Yup.object().shape({
    email: Yup.string().required("Please enter a valid login"),
    password: Yup.string().required("Please provide a password"),
});

// The form lives in a child component (recipe P5, TotpVerifyForm shape) so
// its useForm store is created fresh when the card first renders. Login
// below stays mounted across the token-validation loading and error
// screens; a useForm hoisted into it would capture the pre-token
// defaultValues (empty email) and never show the token owner's address.
function LoginForm({
    initialEmail,
    onSubmit,
    isSubmitting,
    isTokenValidation,
    auth,
    onResendVerification,
    resendVerificationLoading,
    verificationResent,
}) {
    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(validationSchemaLogin),
        defaultValues: {
            email: initialEmail,
            password: "",
        },
    });
    // Destructure the formState flags up front (P1): formState is a
    // subscription proxy and an inline `isValid && isDirty` short-circuits
    // before isDirty while isValid is false, dropping the subscription.
    const { isValid, isDirty } = methods.formState;
    const [showPassword, setShowPassword] = useState(false);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <CardBody className="card-inner-body font-body my-4 pb-2">
                    <div className="px-0">
                        <Typography
                            as="div"
                            className="mt-2 px-4 rounded-2xl"
                        >
                            <RHFInputIcon
                                name="email"
                                iconName="email"
                                label="Email"
                                autoComplete="username"
                                readOnly={isTokenValidation}
                            />
                        </Typography>
                        <Typography
                            as="div"
                            className="px-4 rounded-2xl mt-4"
                        >
                            <RHFInputIcon
                                name="password"
                                type={
                                    showPassword
                                        ? "text"
                                        : "password"
                                }
                                label="Password"
                                iconName="lock"
                                required
                                autoComplete="current-password"
                                className="!text-black !border-b-black font-body"
                            />
                        </Typography>
                        <div className="flex justify-end items-center text-right">
                            <Typography
                                variant="paragraph"
                                className="px-2 text-primary text-sm font-body text-right"
                            >
                                Show Password
                            </Typography>
                            <Checkbox
                                className="checked:bg-primary checked:border-primary checked:before:bg-primary"
                                checked={showPassword}
                                id="showpassword"
                                onChange={() => {
                                    setShowPassword(
                                        (showPassword) =>
                                            !showPassword,
                                    );
                                }}
                            />
                        </div>
                        {auth.error && (
                            <Alert color={"red"}>
                                {auth.errorMessage}
                            </Alert>
                        )}
                        {auth.status === AUTH_STATUS.EMAIL_VERIFICATION_REQUIRED && (
                            <div className="mt-4 text-center">
                                {!verificationResent ? (
                                    <Button
                                        variant="outlined"
                                        color="red"
                                        onClick={onResendVerification}
                                        disabled={resendVerificationLoading}
                                        className="font-body"
                                    >
                                        {resendVerificationLoading ? "Sending..." : "Resend Verification Email"}
                                    </Button>
                                ) : (
                                    <Typography className="text-green-600 font-body">
                                        Verification email sent! Check your inbox.
                                    </Typography>
                                )}
                            </div>
                        )}
                    </div>
                </CardBody>
                <CardFooter className="p-0">
                    <div className="flex justify-center my-4">
                        <Button
                            type="submit"
                            variant="filled"
                            size="lg"
                            className="btn btn--cta rounded-full h-24 w-24 text-2xl"
                            disabled={!(isValid && isDirty) || isSubmitting}
                        >
                            Go
                        </Button>
                    </div>
                    {!isTokenValidation && (
                        <>
                            <Typography className="flex justify-center mt-4 bg-bb font-body text-primary font-normal">
                                New to Gobby?
                            </Typography>

                            <Link
                                className="w-full text-center"
                                to="/register"
                            >
                                <Typography
                                    className="my-2 btn btn--primary text-white"
                                    as="span"
                                >
                                    Create a New Account
                                </Typography>
                            </Link>
                            <Typography
                                as="div"
                                className="btn btn--primary text-center text-white"
                            >
                                <Link
                                    className="text-center"
                                    to="/resetpassword"
                                >
                                    Forgot your details? Click here.
                                </Link>
                            </Typography>
                        </>
                    )}
                </CardFooter>
            </form>
        </FormProvider>
    );
}

export default function Login() {
    const auth = useSelector(authSelector);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const route = searchParams.get('route');
    const token = searchParams.get('token');
    const { isBlocked: loginBlocked } = useRateLimit();
    const [showTOTPDialog, setShowTOTPDialog] = useState(false);
    const [showSetupTOTPDialog, setShowSetupTOTPDialog] = useState(false);
    const [secret32, setSecret32] = useState(null);
    const [secretCode, setSecretCode] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Synchronous lock against double-submit. The ref holds across a same-tick
    // double-click where the isSubmitting state would still read false.
    const submittingRef = useRef(false);
    const [userEmail, setUserEmail] = useState(null);
    const [isTokenValidation, setIsTokenValidation] = useState(false);
    const [tokenValidationLoading, setTokenValidationLoading] = useState(false);
    const [tokenValidationError, setTokenValidationError] = useState('');
    const [resendVerificationLoading, setResendVerificationLoading] = useState(false);
    const [verificationResent, setVerificationResent] = useState(false);

    const handleSuccessfulLogin = () => {
        if (route === 'subscribe') {
            navigate('/?openSubscription=true');
        } else if (route) {
            navigate('/' + route);
        } else {
            navigate('/');
        }
    };

    const handleEmailValidationToken = async () => {
        if (!token) return;

        setTokenValidationLoading(true);
        setTokenValidationError('');

        try {
            // Validate token without consuming it on load.
            const tokenResponse = await fetcherSimple(`/v2/token/${encodeURIComponent(token)}`, 'GET');

            if (!tokenResponse || !tokenResponse.valid) {
                throw new Error('Invalid or expired token');
            }

            if (tokenResponse.used) {
                throw new Error('Invalid or expired token');
            }

            if (tokenResponse.purpose !== 'signup') {
                throw new Error('Invalid or expired token');
            }

            setUserEmail(tokenResponse.owner);
            setIsTokenValidation(true);

        } catch (error) {
            // Redact live token from console.
            console.error('Token validation error');
            setTokenValidationError('Invalid or expired validation link');
        } finally {
            setTokenValidationLoading(false);
        }
    };

    const checkTOTPRequired = async () => {
        try {
            const response = await fetcherSimple("/v2/auth/session", "GET");
            if (response && response.otp_enabled === false) {
                // User doesn't have TOTP enabled, show setup dialog
                setUserEmail(response.email);
                const setupResponse = await fetcherSimple("/v2/auth/setup-totp", "GET");
                if (setupResponse.secret) {
                    setSecret32(setupResponse.secret32);
                    setSecretCode(setupResponse.secret);
                    setShowSetupTOTPDialog(true);
                }
            } else {
                handleSuccessfulLogin();
            }
        } catch (error) {
            console.error("Error checking TOTP status:", error);
            handleSuccessfulLogin();
        }
    };

    // Shared handler for the `next` field returned by POST /v2/auth/login,
    // used by both login flows.
    const followLoginNext = async (loginResponse, email) => {
        if (loginResponse.next === AUTH_ACTION.SETUP_TOTP) {
            const response = await fetcherSimple("/v2/auth/setup-totp", "GET");
            if (response.secret) {
                setUserEmail(email);
                setSecret32(response.secret32);
                setSecretCode(response.secret);
                setShowSetupTOTPDialog(true);
            }
        } else if (loginResponse.next === AUTH_ACTION.CHALLENGE_TOTP) {
            setShowTOTPDialog(true);
        } else {
            await checkTOTPRequired();
        }
    };

    const handleLoginSubmit = async (values) => {
        if (loginBlocked) {
            toast.error('Too many attempts. Please try again later.');
            return;
        }
        // In-flight guard: blocks a same-tick double-click that the
        // isSubmitting state can't catch yet (it hasn't re-rendered).
        if (submittingRef.current) {
            return;
        }
        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            const sanitisedValues = {
                email: sanitiseInput(values.email),
                // Password verbatim, so a credential set outside this app can
                // authenticate. See #1097 / gobby-app/gobbyapi#466.
                password: values.password,
            };

            if (isTokenValidation) {
                // 401 bails to allow retry; 409 falls through to login since
                // a consumed signup token means email is already validated.
                let ackFailed = false;
                try {
                    const ackResponse = await fetcherFull('/v2/token', 'POST', {
                        token,
                        password: sanitisedValues.password,
                    });
                    if (ackResponse && ackResponse.valid === false) {
                        toast.error("Invalid or expired validation link");
                        return;
                    }
                } catch (ackError) {
                    if (isApiError(ackError) && ackError.status === 401) {
                        toast.error("Incorrect password. Please try again.");
                        ackFailed = true;
                    } else if (isApiError(ackError) && ackError.status === 409) {
                        // 409 falls through: email is already validated.
                    } else {
                        // Non-401/409 failure belongs to token endpoint, not login dispatch.
                        reportError(ackError, { context: "signup token acknowledge" });
                        toast.error(getApiErrorMessage(classifyError(ackError)));
                        return;
                    }
                }

                if (!ackFailed) {
                    const loginResponse = await dispatch(login({
                        email: userEmail,
                        password: sanitisedValues.password,
                    })).unwrap();
                    await followLoginNext(loginResponse, userEmail);
                }
            } else {
                // Normal login flow
                const loginResponse = await dispatch(login(sanitisedValues)).unwrap();
                await followLoginNext(loginResponse, sanitisedValues.email);
            }
        } catch (rejectedValueOrSerializedError) {
            // Check if this is an email verification error (issue #943)
            // The auth slice will handle setting the appropriate status
            if (rejectedValueOrSerializedError?.error === "EMAIL_NOT_VERIFIED") {
                return;
            }
            toast.error("Error with logging in to server");
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleResendVerification = async () => {
        if (resendVerificationLoading || !auth.unverifiedEmail) return;

        setResendVerificationLoading(true);
        const resendToast = toast.loading("Sending verification email...");

        try {
            const requestBody = {
                email: auth.unverifiedEmail,
                frontend_url: window.location.origin
            };
            if (route) {
                requestBody.route = route;
            }
            const response = await fetcherFull('/v2/user/resend', 'POST', requestBody);
            if (response && response.success) {
                setVerificationResent(true);
                toast.update(resendToast, {
                    render: "Verification email sent! Please check your inbox.",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                });
            } else {
                throw new Error("Failed to send email");
            }
        } catch (error) {
            toast.update(resendToast, {
                render: "Failed to send verification email. Please try again later.",
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
        } finally {
            setResendVerificationLoading(false);
        }
    };

    const handleTotpSetupVerify = async (code, secret) => {
        // Route through the thunk (not a direct POST) so auth status flips to LOGGED_IN.
        await dispatch(totpSetup({ secret, code })).unwrap();
        setShowSetupTOTPDialog(false);
        handleSuccessfulLogin();
    };

    const handleTOTPVerifySuccess = async () => {
        // After TOTP verification, check if user still needs to complete TOTP setup
        try {
            const response = await fetcherSimple("/v2/auth/session", "GET");
            if (response && response.otp_enabled === false) {
                // User doesn't have TOTP enabled, show setup dialog
                setUserEmail(response.email);
                const setupResponse = await fetcherSimple("/v2/auth/setup-totp", "GET");
                if (setupResponse.secret) {
                    setSecret32(setupResponse.secret32);
                    setSecretCode(setupResponse.secret);
                    setShowSetupTOTPDialog(true);
                }
            } else {
                handleSuccessfulLogin();
            }
        } catch (error) {
            console.error("Error checking TOTP status after verify:", error);
            handleSuccessfulLogin();
        }
    };

    useEffect(() => {
        // fetch session and see if logged in
        if (auth.status === AUTH_STATUS.UNKNOWN) {
            dispatch(fetchSession());
        }
    }, [auth.status, dispatch]);

    useEffect(() => {
        if (token) {
            handleEmailValidationToken();
        }
    }, [token]);

    // Token validation loading state
    if (tokenValidationLoading) {
        return (
            <div className="flex flex-col min-w-screen min-h-screen bg-white justify-center items-center">
                <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                    <Image
                        className="sm:w-[250px] w-[0px]"
                        src={logo}
                        rounded={false}
                        raised={false}
                        alt={"Gobby Logo"}
                    />
                </div>
                <Typography className="font-display text-center text-primary">
                    Validating your email...
                </Typography>
            </div>
        );
    }

    // Token validation error state
    if (tokenValidationError) {
        return (
            <div className="flex flex-col min-w-screen min-h-screen bg-white">
                <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                    <Image
                        className="sm:w-[250px] w-[0px]"
                        src={logo}
                        rounded={false}
                        raised={false}
                        alt={"Gobby Logo"}
                    />
                </div>
                <div className="flex min-h-fit justify-center">
                    <Card
                        className="card-inner filter-none sm:p-8 sm:pt-6 p-4"
                        shadow={false}
                    >
                        <Typography className="font-display text-center md:text-xl lg:content-center md:mt-2 text-primary">
                            Invalid Validation Link
                        </Typography>
                        <CardBody className="card-inner-body font-body my-4 pb-2">
                            <Typography className="text-center mb-4">
                                {tokenValidationError}
                            </Typography>
                            <Typography className="text-center mb-4">
                                Please check your email for a valid link or request a new one.
                            </Typography>
                        </CardBody>
                        <CardFooter className="p-0">
                            <div className="flex justify-center">
                                <Link to="/login">
                                    <Button
                                        variant="filled"
                                        size="lg"
                                        className="btn btn--primary"
                                    >
                                        Return to Login
                                    </Button>
                                </Link>
                            </div>
                        </CardFooter>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col min-w-screen min-h-screen bg-white">
                <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                    <Image
                        className="sm:w-[250px] w-[0px]"
                        src={logo}
                        rounded={false}
                        raised={false}
                        alt={"Gobby Logo"}
                    />
                </div>
                <div className="flex min-h-fit justify-center">
                    <Card
                        className="card-inner filter-none sm:p-8 sm:pt-6 p-4"
                        shadow={false}
                    >
                        <Typography className="font-display text-center md:text-xl lg:content-center md:mt-2 text-primary">
                            {isTokenValidation ? 'Complete Your Registration' : 'Sign in to Start Using Gobby'}
                            <Trademark />
                        </Typography>
                        {isTokenValidation && (
                            <Typography className="text-center mb-4 font-body">
                                Confirm your email by entering your password to set up 2FA.
                            </Typography>
                        )}
                        <LoginForm
                            initialEmail={isTokenValidation ? userEmail || "" : ""}
                            onSubmit={handleLoginSubmit}
                            isSubmitting={isSubmitting}
                            isTokenValidation={isTokenValidation}
                            auth={auth}
                            onResendVerification={handleResendVerification}
                            resendVerificationLoading={resendVerificationLoading}
                            verificationResent={verificationResent}
                        />
                    </Card>
                </div>
            </div>
            <TotpSetupDialog
                open={showSetupTOTPDialog}
                onClose={() => setShowSetupTOTPDialog(false)}
                onSetupSuccess={() => {}}
                onSetupCancel={() => setShowSetupTOTPDialog(false)}
                secretCode={secretCode}
                secret32={secret32}
                userEmail={userEmail}
                onVerify={handleTotpSetupVerify}
            />
            <TotpVerifyDialog
                open={showTOTPDialog}
                onClose={() => setShowTOTPDialog(false)}
                onVerifySuccess={handleTOTPVerifySuccess}
                customVerifyFunction={async (totp) => {
                    // Strip whitespace before dispatching verification.
                    await dispatch(totpVerify({ code: totp?.replace(/\s+/g, "") })).unwrap();
                }}
            />
        </>
    );
}
